import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addDays, format, parseISO, startOfDay } from 'date-fns';
import { ChevronLeft } from 'lucide-react';
import { useLocationSports } from '@/api/hooks/useLocations';
import { useAvailability } from '@/api/hooks/useAvailability';
import { useBookingPolicy } from '@/api/hooks/useBookingPolicy';
import { useTenantBySlug } from '@/api/hooks/useTenantBySlug';
import { useVenueBySlug } from '@/api/hooks/useVenues';
import { useVipStatus } from '@/api/hooks/useVipStatus';
import { useAuth } from '@/providers/AuthProvider';
import { DEFAULT_TENANT_SLUG, DEFAULT_VENUE_SLUG } from '@/lib/constants';
import { useVenueContext } from '@/providers/VenueProvider';
import type { Slot, UnitSlot } from '@/api/types';
import { Skeleton } from '@/components/ui/skeleton';
import DateSelector from '@/components/booking/DateSelector';
import AvailabilityGrid, {
  timeToMinutes,
  minutesToTime,
  calculatePrice,
  checkConsecutiveAvailability,
} from '@/components/booking/AvailabilityGrid';
import BookingCard from '@/components/booking/BookingCard';
import { useAvailableAddons } from '@/api/hooks/useAddons';
import type { AddonSelection } from '@/api/hooks/useAddons';
import { useVenueSlugCapture } from '@/hooks/useVenueSlugCapture';

export default function AvailabilityPage() {
  useVenueSlugCapture();
  const { sportCode, slug } = useParams<{ sportCode: string; slug: string }>();
  const { currentSlug } = useVenueContext();
  const venueSlug = slug ?? currentSlug;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const { data: venue } = useVenueBySlug(DEFAULT_VENUE_SLUG);
  const locationId = venue?.id;
  const { data: tenant } = useTenantBySlug(DEFAULT_TENANT_SLUG);
  const { data: vipStatus } = useVipStatus(tenant?.id, isAuthenticated);
  const { data: bookingPolicy } = useBookingPolicy(locationId, sportCode);
  const { data: locationData } = useLocationSports(locationId);
  const sport = locationData?.sports.find((s) => s.code === sportCode);

  // Customer's effective booking horizon. VIP customers get the
  // tenant-scoped `vipBookingWindowDays`; regular customers get the
  // per-sport `maxAdvanceBookingDaysRegular`. While either query is
  // loading we fall back to the regular default (14) — keeps the
  // calendar usable without flashing a wider/narrower window.
  const effectiveMaxAdvanceDays = useMemo(() => {
    if (!bookingPolicy) return 14;
    return vipStatus?.isVip
      ? bookingPolicy.vipBookingWindowDays
      : bookingPolicy.maxAdvanceBookingDaysRegular;
  }, [bookingPolicy, vipStatus?.isVip]);

  // Sport config
  const bookingIncrement = 15;
  const minDuration = sport?.allowedDurations[0] ?? 60;
  const maxDuration = sport?.allowedDurations[sport.allowedDurations.length - 1] ?? 120;
  const defaultDuration = minDuration;
  const isPerPerson = sport?.pricingModel === 'PER_PERSON_PER_HOUR';
  const minPlayers = sport?.minPlayers ?? 1;
  const maxPlayers = sport?.maxPlayers ?? 6;

  const [date, setDate] = useState<Date>(() => {
    const dateParam = searchParams.get('date');
    return dateParam ? parseISO(dateParam) : new Date();
  });
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(
    searchParams.get('unit'),
  );
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(
    searchParams.get('startTime'),
  );
  const [selectedDuration, setSelectedDuration] = useState<number>(defaultDuration);
  const [playerCount, setPlayerCount] = useState<number>(minPlayers);
  const [addonSelections, setAddonSelections] = useState<AddonSelection[]>([]);

  const dateStr = format(date, 'yyyy-MM-dd');

  // Pure function of `(selectedDate, today)` — the strip stays anchored
  // on today for the common case, but if the customer picks a date past
  // today + 7 (via the calendar popover) the strip re-anchors so the
  // selection stays visible. `startOfDay` normalises so comparisons
  // ignore the local time-of-day component.
  const anchorDate = useMemo(() => {
    const today = startOfDay(new Date());
    const selected = startOfDay(date);
    const stripEnd = addDays(today, 7);
    if (selected >= today && selected < stripEnd) return today;
    return selected > today ? selected : today;
  }, [date]);

  // Fetch with bookingIncrement as duration to get every free cell at the
  // smallest granularity. The backend accepts any increment-multiple for
  // availability queries; booking creation enforces allowedDurations separately.
  const { data: availability, isLoading } = useAvailability({
    locationId: locationId ?? '',
    sportCode: sportCode || '',
    date: dateStr,
    duration: bookingIncrement,
    playerCount,
  });

  // Transform auto-assign flat slots into a single pseudo-unit for the grid
  const gridUnits: UnitSlot[] = useMemo(() => {
    if (!availability) return [];
    if (availability.customerSelectsUnit && availability.units) {
      return availability.units;
    }
    if (!availability.customerSelectsUnit && availability.slots) {
      return [{
        unitId: 'auto',
        unitName: sport?.unitTermPlural || 'Available',
        features: {},
        slots: availability.slots,
      }];
    }
    return [];
  }, [availability, sport?.unitTermPlural]);

  // Build slot lookup for quick availability checking
  const unitSlotMaps = useMemo(() => {
    const map = new Map<string, Map<string, Slot>>();
    gridUnits.forEach((unit) => {
      const unitMap = new Map<string, Slot>();
      unit.slots.forEach((slot) => unitMap.set(slot.startTimeLocal, slot));
      map.set(unit.unitId, unitMap);
    });
    return map;
  }, [gridUnits]);

  // Check if extension is possible (next cell is available)
  const canExtend = useMemo(() => {
    if (!selectedUnitId || !selectedStartTime) return false;
    const unitSlots = unitSlotMaps.get(selectedUnitId);
    if (!unitSlots) return false;
    const endMinutes = timeToMinutes(selectedStartTime) + selectedDuration;
    const endTimeKey = minutesToTime(endMinutes);
    return unitSlots.has(endTimeKey);
  }, [selectedUnitId, selectedStartTime, selectedDuration, unitSlotMaps]);

  // Calculate price for current selection
  const currentPrice = useMemo(() => {
    if (!selectedUnitId || !selectedStartTime) return 0;
    const unitSlots = unitSlotMaps.get(selectedUnitId);
    if (!unitSlots) return 0;
    return calculatePrice(selectedStartTime, selectedDuration, bookingIncrement, unitSlots);
  }, [selectedUnitId, selectedStartTime, selectedDuration, bookingIncrement, unitSlotMaps]);

  // Calculate end time
  const selectedEndTime = useMemo(() => {
    if (!selectedStartTime) return null;
    return minutesToTime(timeToMinutes(selectedStartTime) + selectedDuration);
  }, [selectedStartTime, selectedDuration]);

  // Fetch addon availability for the selected time window
  const selectedSlotUTC = useMemo(() => {
    if (!selectedStartTime || !selectedUnitId) return null;
    const unitSlots = unitSlotMaps.get(selectedUnitId);
    return unitSlots?.get(selectedStartTime)?.startTime || null;
  }, [selectedStartTime, selectedUnitId, unitSlotMaps]);

  const { data: addonData } = useAvailableAddons(
    selectedSlotUTC && locationId
      ? {
          locationId,
          sportCode: sportCode || '',
          startTime: selectedSlotUTC,
          endTime: new Date(
            new Date(selectedSlotUTC).getTime() + selectedDuration * 60000,
          ).toISOString(),
        }
      : null,
  );

  // Clear addon selections when slot changes
  useEffect(() => {
    setAddonSelections([]);
  }, [selectedUnitId, selectedStartTime]);

  // Sync state to URL search params
  useEffect(() => {
    const params: Record<string, string> = { date: dateStr };
    if (selectedUnitId) params.unit = selectedUnitId;
    if (selectedStartTime) params.startTime = selectedStartTime;
    setSearchParams(params, { replace: true });
  }, [dateStr, selectedUnitId, selectedStartTime, setSearchParams]);

  const handleDateChange = (d: Date | undefined) => {
    if (d) {
      setDate(d);
      setSelectedUnitId(null);
      setSelectedStartTime(null);
    }
  };

  const handleSelectSlot = (unitId: string, startTime: string) => {
    setSelectedUnitId(unitId);
    setSelectedStartTime(startTime);

    // Set default duration, capping if not enough consecutive slots
    let dur = defaultDuration;
    const unitSlots = unitSlotMaps.get(unitId);
    if (unitSlots) {
      while (dur > bookingIncrement) {
        if (checkConsecutiveAvailability(startTime, dur, bookingIncrement, unitSlots)) break;
        dur -= bookingIncrement;
      }
    }
    setSelectedDuration(dur);
  };

  const handleIncrease = () => {
    if (canExtend && selectedDuration + bookingIncrement <= maxDuration) {
      setSelectedDuration((prev) => prev + bookingIncrement);
    }
  };

  const handleDecrease = () => {
    if (selectedDuration - bookingIncrement >= minDuration) {
      setSelectedDuration((prev) => prev - bookingIncrement);
    }
  };

  const handleClear = () => {
    setSelectedUnitId(null);
    setSelectedStartTime(null);
  };

  const isAutoAssign = availability ? !availability.customerSelectsUnit : false;

  const handleBook = () => {
    if (!selectedStartTime || !selectedUnitId || !availability || !locationId) return;
    // Get the UTC startTime from the slot object
    const unitSlots = unitSlotMaps.get(selectedUnitId);
    const slot = unitSlots?.get(selectedStartTime);
    const bookingParams: Record<string, unknown> = {
      locationId,
      sportCode,
      sportName: sport?.name,
      date: dateStr,
      startTime: slot?.startTime || selectedStartTime,
      duration: selectedDuration,
      playerCount,
    };
    // Only pass unitId for customer-selects-unit sports
    if (!isAutoAssign) {
      bookingParams.unitId = selectedUnitId;
    }
    if (addonSelections.length > 0) {
      bookingParams.addons = addonSelections;
    }
    sessionStorage.setItem('checkoutParams', JSON.stringify(bookingParams));
    navigate(`/venue/${venueSlug}/book/${sportCode}/checkout`, { state: bookingParams });
  };

  const selectedUnitName =
    gridUnits.find((u) => u.unitId === selectedUnitId)?.unitName || '';

  return (
    <div className="py-6">
      {/* Back + sport name */}
      <div className="px-4">
        <Link
          to={`/venue/${venueSlug}/book`}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('common.back')}
        </Link>

        <div className="mb-4 flex items-center gap-3">
          {sport && <span className="text-3xl">{sport.icon}</span>}
          <h1 className="text-2xl font-semibold">{t(`sport_${sportCode}`, { defaultValue: sport?.name || sportCode })}</h1>
        </div>
      </div>

      {/* Date selector */}
      <div className="px-4 mb-4">
        <DateSelector
          selectedDate={date}
          onSelect={(d) => handleDateChange(d)}
          anchorDate={anchorDate}
          effectiveMaxAdvanceDays={effectiveMaxAdvanceDays}
        />
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="px-4">
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-24 rounded-lg" />
            ))}
          </div>
        </div>
      )}

      {/* #89 — a paused facility shows an honest unavailable message in place of the (empty) grid,
          so a deep-linked date doesn't read as "fully booked". No return-time promise (auto-unpause
          removed in #145). */}
      {availability?.bookingsPaused && (
        <div className="px-4">
          <div className="rounded-2xl border border-amber-300 bg-amber-50 px-6 py-12 text-center">
            <p className="text-lg font-semibold text-amber-900">{t('booking.facility_paused_title')}</p>
            <p className="mt-2 text-sm text-amber-800">{t('booking.facility_paused_body')}</p>
          </div>
        </div>
      )}

      {/* Availability grid — unified for all sports */}
      {availability && !availability.bookingsPaused && gridUnits.length > 0 && (
        <div className="px-4">
          <AvailabilityGrid
            units={gridUnits}
            selectedUnitId={selectedUnitId}
            selectedStartTime={selectedStartTime}
            selectedDuration={selectedDuration}
            bookingIncrement={bookingIncrement}
            minDuration={minDuration}
            maxDuration={maxDuration}
            currency={availability.currency}
            date={dateStr}
            onSelectSlot={handleSelectSlot}
            onDurationChange={setSelectedDuration}
          />
        </div>
      )}

      {/* Floating booking card — only when grid slot selected */}
      {selectedUnitId && selectedStartTime && selectedEndTime && (
        <BookingCard
          unitName={selectedUnitName}
          startTime={selectedStartTime}
          endTime={selectedEndTime}
          duration={selectedDuration}
          price={currentPrice}
          currency={availability?.currency || 'CAD'}
          minDuration={minDuration}
          maxDuration={maxDuration}
          canExtend={canExtend}
          playerCount={playerCount}
          minPlayers={minPlayers}
          maxPlayers={maxPlayers}
          isPerPerson={isPerPerson}
          addons={addonData || []}
          addonSelections={addonSelections}
          onAddonChange={setAddonSelections}
          onIncrease={handleIncrease}
          onDecrease={handleDecrease}
          onPlayerCountChange={setPlayerCount}
          onBook={handleBook}
          onCancel={handleClear}
        />
      )}

      {/* Bottom padding when floating card is visible */}
      {selectedUnitId && selectedStartTime && <div className="h-36" />}
    </div>
  );
}
